import type { Dataset, DatasetRow } from './dataset.js';

/**
 * Checking that the local replica really does match the server (ADR-0025).
 *
 * A watermark delta is only correct if no change was ever missed, and that is
 * a property of every write path in the app agreeing to announce itself — an
 * assumption that had already been broken four times over before anybody
 * looked. A rule that has to be remembered is not a guarantee. This is the
 * guarantee: fold what is held, compare, and refetch whatever disagrees.
 *
 * It repairs drift whatever caused it — a missed event, a clock that went
 * backwards, a pruned cursor, a hand-edited row, or a fifth write path
 * somebody adds next year without reading any of this.
 */

/** The same CRC-32 MySQL's `CRC32()` computes, so the two folds are comparable. */
const CRC_TABLE = (() => {
  const table = new Int32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c;
  }
  return table;
})();

export function crc32(text: string): number {
  let crc = -1;
  // The server hashes bytes; these strings are uuids and digits, so every
  // character is one byte and the two agree without an encoder.
  for (let i = 0; i < text.length; i += 1) {
    crc = (crc >>> 8) ^ CRC_TABLE[(crc ^ text.charCodeAt(i)) & 0xff]!;
  }
  return (crc ^ -1) >>> 0;
}

export interface EntityDigest {
  count: number;
  checksum: number;
}

/**
 * Fold one entity's rows the way the server folds them:
 * `BIT_XOR(CRC32(CONCAT(id, ':', version)))`.
 *
 * `id` and `version` and nothing else, deliberately. Both sides hold those as
 * exactly the same text and the same integer — no decimal formatting, no date
 * rendering, no collation to disagree about. A digest that could differ for a
 * formatting reason would re-snapshot for ever, which is worse than not
 * checking at all.
 */
export function foldEntity(rows: readonly DatasetRow[]): EntityDigest {
  let checksum = 0;
  for (const row of rows) checksum ^= crc32(`${row.id}:${row.version ?? 0}`);
  return { count: rows.length, checksum: checksum >>> 0 };
}

export interface VerifyOptions {
  dataset: Dataset;
  fetchDigest: () => Promise<{ entities: Record<string, EntityDigest> }>;
  /**
   * One page of one entity's rows, for repairing the ones that disagree.
   * `next.after` is echoed back until the server stops offering one.
   */
  fetchEntity: (entity: string, after?: string) => Promise<{
    rows: DatasetRow[]; next?: { after: string };
  }>;
}

/** See `MAX_PAGES` in replicaSync: a guard against a page that never advances. */
const MAX_PAGES = 20_000;

/**
 * Compare, and repair what differs. Returns the entities it had to refetch.
 *
 * An entity the server reports and the device has never heard of counts as a
 * mismatch — that is a cold store, or one that never finished filling.
 */
export async function verifyAndRepair(opts: VerifyOptions): Promise<string[]> {
  const { entities } = await opts.fetchDigest();
  const repaired: string[] = [];
  for (const [entity, theirs] of Object.entries(entities)) {
    // As the server holds it, not as this device's unsent work shows it.
    const mine = foldEntity(opts.dataset.serverRows(entity));
    if (mine.count === theirs.count && mine.checksum === theirs.checksum) continue;
    // Collected in full before anything is replaced. `replaceEntity` is the
    // one atomic step here — swapping in a first page and topping it up would
    // leave the entity visibly short of rows in between, and short for good
    // if the next request never came back.
    let page = await opts.fetchEntity(entity);
    const rows = [...page.rows];
    for (let seen = 0; page.next; seen += 1) {
      if (seen >= MAX_PAGES) throw new Error(`replica: ${entity} repair did not terminate`);
      page = await opts.fetchEntity(entity, page.next.after);
      rows.push(...page.rows);
    }
    await opts.dataset.replaceEntity(entity, rows);
    repaired.push(entity);
  }
  return repaired;
}
